import React, { useState, useEffect } from 'react';
import { authAPI } from '../services/api';

function DependentAccountsSection({ user }) {
  const [dependents, setDependents] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequestForm, setShowRequestForm] = useState(false);
  
  // Request form state
  const [requestDependents, setRequestDependents] = useState([{ displayName: '', age: '', notes: '' }]);
  const [reason, setReason] = useState('');
  const [requestError, setRequestError] = useState('');
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [depsResponse, reqsResponse] = await Promise.all([
        authAPI.getDependents(),
        authAPI.getDependentRequests()
      ]);

      if (depsResponse.success) {
        setDependents(depsResponse.data.dependents || []);
      }
      if (reqsResponse.success) {
        setRequests(reqsResponse.data.requests || []);
      }
    } catch (error) {
      console.error('Failed to load dependent data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDependentField = () => {
    setRequestDependents([...requestDependents, { displayName: '', age: '', notes: '' }]);
  };

  const handleRemoveDependentField = (index) => {
    setRequestDependents(requestDependents.filter((_, i) => i !== index));
  };

  const handleDependentChange = (index, field, value) => {
    const updated = [...requestDependents];
    updated[index][field] = value;
    setRequestDependents(updated);
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    setRequestError('');

    // Validate
    const validDependents = requestDependents.filter(d => d.displayName.trim());
    if (validDependents.length === 0) {
      setRequestError('Please add at least one dependent with a display name');
      return;
    }

    setRequesting(true);
    try {
      const response = await authAPI.requestDependents({
        dependents: validDependents.map(d => ({
          displayName: d.displayName.trim(),
          age: d.age ? parseInt(d.age) : undefined,
          notes: d.notes.trim()
        })),
        reason: reason.trim()
      });

      if (response.success) {
        alert('Dependent account request submitted! Admin will review your request.');
        setShowRequestForm(false);
        setRequestDependents([{ displayName: '', age: '', notes: '' }]);
        setReason('');
        loadData(); // Refresh to show new request
      } else {
        setRequestError(response.error?.message || 'Failed to submit request');
      }
    } catch (error) {
      setRequestError(error.response?.data?.error?.message || 'Failed to submit request');
    } finally {
      setRequesting(false);
    }
  };

  const hasPendingRequest = requests.some(r => r.status === 'pending');

  if (loading) {
    return (
      <div className="card">
        <h3 style={{ marginTop: 0 }}>👨‍👩‍👧‍👦 Dependent Accounts</h3>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>👨‍👩‍👧‍👦 Dependent Accounts</h3>
      <p style={{ color: '#666', fontSize: '0.95rem', marginBottom: '1.5rem' }}>
        Manage dependent accounts for family members (e.g., children) who can make picks independently without needing their own login.
      </p>

      {/* Existing Dependents */}
      {dependents.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h4 style={{ fontSize: '1rem', marginBottom: '0.75rem' }}>Your Dependents</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {dependents.map((dep) => (
              <div
                key={dep.userId}
                style={{
                  padding: '1rem',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  background: '#f9f9f9'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div>
                    <strong style={{ fontSize: '1.05rem' }}>{dep.displayName}</strong>
                    <p style={{ margin: '0.25rem 0', fontSize: '0.85rem', color: '#666' }}>
                      @{dep.username}
                    </p>
                    <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>
                      Picks: {dep.totalPicks} • Winnings: ${dep.totalWinnings?.toFixed(2) || '0.00'} • Win Rate: {dep.winRate?.toFixed(1) || '0'}%
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Requests Status */}
      {requests.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h4 style={{ fontSize: '1rem', marginBottom: '0.75rem' }}>Request History</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {requests.map((req) => (
              <div
                key={req.requestId}
                style={{
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  background: req.status === 'approved' ? '#e8f5e9' : req.status === 'rejected' ? '#ffebee' : '#fff3e0'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong>{req.requestedDependents.length} dependent(s)</strong>
                    <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
                      • {new Date(req.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <span
                    style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '0.85rem',
                      fontWeight: 'bold',
                      background: req.status === 'approved' ? '#4caf50' : req.status === 'rejected' ? '#f44336' : '#ff9800',
                      color: 'white'
                    }}
                  >
                    {req.status}
                  </span>
                </div>
                {req.reviewNotes && (
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem', fontStyle: 'italic', color: '#555' }}>
                    {req.reviewNotes}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Request Form */}
      {!showRequestForm && !hasPendingRequest && (
        <button
          onClick={() => setShowRequestForm(true)}
          className="submit-button"
          style={{ background: '#2196F3' }}
        >
          + Request Dependent Account(s)
        </button>
      )}

      {hasPendingRequest && !showRequestForm && (
        <div style={{ padding: '1rem', background: '#fff3e0', borderRadius: '8px', border: '1px solid #ff9800' }}>
          <strong>⏳ Pending Request</strong>
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>
            You have a pending dependent account request. Please wait for admin approval.
          </p>
        </div>
      )}

      {showRequestForm && (
        <form onSubmit={handleSubmitRequest}>
          <div style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ fontSize: '1rem', marginBottom: '1rem' }}>Request Dependent Accounts</h4>

            {requestError && (
              <div className="error-message" style={{ marginBottom: '1rem' }}>
                {requestError}
              </div>
            )}

            {requestDependents.map((dep, index) => (
              <div
                key={index}
                style={{
                  padding: '1rem',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  background: '#f9f9f9'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <strong>Dependent #{index + 1}</strong>
                  {requestDependents.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveDependentField(index)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#f44336',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: 'bold'
                      }}
                    >
                      ✕ Remove
                    </button>
                  )}
                </div>

                <div className="form-group">
                  <label>Display Name *</label>
                  <input
                    type="text"
                    value={dep.displayName}
                    onChange={(e) => handleDependentChange(index, 'displayName', e.target.value)}
                    placeholder="e.g., Child One"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Age (optional)</label>
                  <input
                    type="number"
                    value={dep.age}
                    onChange={(e) => handleDependentChange(index, 'age', e.target.value)}
                    placeholder="e.g., 10"
                    min="1"
                    max="100"
                  />
                </div>

                <div className="form-group">
                  <label>Notes (optional)</label>
                  <input
                    type="text"
                    value={dep.notes}
                    onChange={(e) => handleDependentChange(index, 'notes', e.target.value)}
                    placeholder="e.g., My daughter"
                  />
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={handleAddDependentField}
              style={{
                background: 'none',
                border: '2px dashed #2196F3',
                color: '#2196F3',
                padding: '0.75rem',
                borderRadius: '8px',
                width: '100%',
                cursor: 'pointer',
                fontWeight: 'bold',
                marginBottom: '1rem'
              }}
            >
              + Add Another Dependent
            </button>

            <div className="form-group">
              <label>Reason for Request (optional)</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Let us know why you need dependent accounts (e.g., 'My kids want to participate')"
                rows="3"
                style={{ resize: 'vertical', fontFamily: 'inherit' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                type="submit"
                className="submit-button"
                disabled={requesting}
              >
                {requesting ? 'Submitting...' : 'Submit Request'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowRequestForm(false);
                  setRequestDependents([{ displayName: '', age: '', notes: '' }]);
                  setReason('');
                  setRequestError('');
                }}
                style={{
                  background: '#999',
                  border: 'none',
                  color: 'white',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}

export default DependentAccountsSection;
